import { type Kmat, type LinearCstic, type LinearSubstep } from "~/features/configurator/Linear/models";
import { isTileStandard } from "~/features/configurator/common/tileUtils";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import type { LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import type { WithTranslation } from "react-i18next";
import { prepareSummaryDataName, prepareSummaryDataValue, prepareSummaryDataValues } from "../common/v2/gaHelper";
import { removeMultipleTags } from "~/common/utils/domUtils";
import { isNonNull, toMapFn } from "~/common/utils/reducers";
import { formatCsticValueAsNumber } from "./components/useMinMaxCsticLabel";
import instance from "~/utils/i18n";

export interface StepSummaryData {
  name: string;
  gaName: string;
  tables: (SummaryDataTableV2 | CarriageDetailsSummaryData)[];
}

export interface CarriageDetailsSummaryData {
  name: string;
  gaName: string;
  tables: SummaryDataTableV2[];
}

export type SummaryDataTableV2 = SimpleSummaryDataTableV2 | MultiColumnSummaryDataTableV2;

export interface SimpleSummaryDataTableV2 {
  name: string;
  gaName: string;
  entries: SummaryValueV2[];
}

export interface MultiColumnSummaryDataTableV2 {
  name: string;
  gaName: string;
  entries: SummaryDataTableColumnV2[];
}

export interface SummaryDataTableColumnV2 {
  name: string;
  gaName: string;
  values: SummaryValueV2[];
}

export interface SummaryValueV2 {
  name: string;
  gaName: string;
  value: string;
  isStandard?: boolean;
  extra?: string;
}

export interface SummaryData extends SummaryValue {
  name: string;
}

export interface SummaryValue {
  value: string;
  isStandard?: boolean;
  extra?: string;
}

export interface RailKmatSummaryData {
  name: string;
  rail: SummaryValue[];
}

export interface CarriageKmatSummaryData {
  name: string;
  rail: {
    carriage: SummaryValue[];
  }[];
}

export type GaSelectionData = {
  [key: string]: string | GaSelectionData;
};

export function transformSummaryDataToV2(item: SummaryData): SummaryValueV2 {
  return {
    ...item,
    gaName: prepareSummaryDataName(item),
  };
}

export function transformRailSummaryDataToV2(source: RailKmatSummaryData[]): SummaryDataTableV2 {
  const railAmount = source[0].rail.length;

  const columns: SummaryDataTableColumnV2[] = [];
  for (let railIndex = 0; railIndex < railAmount; railIndex++) {
    const values: SummaryValueV2[] = source.map((item) => {
      const { value, extra, isStandard } = item.rail[railIndex];
      return {
        name: item.name,
        gaName: item.name,
        value,
        extra,
        isStandard,
      };
    });

    const table: SummaryDataTableColumnV2 = {
      name: instance.t("linearConfigSummary.step.headerGuidewaySet", { count: railIndex + 1 }),
      gaName: `guideway_set_${railIndex + 1}`,
      values,
    };
    columns.push(table);
  }

  return {
    name: "",
    gaName: "configured_guideway_data",
    entries: columns,
  };
}

export function transformCarriageSummaryDataToV2(source: CarriageKmatSummaryData[]): CarriageDetailsSummaryData {
  const railAmount = source[0].rail.length;
  const carriagesPerRail = source[0].rail[0].carriage.length;

  const tables: SummaryDataTableV2[] = [];
  for (let railIndex = 0; railIndex < railAmount; railIndex++) {
    const columns: SummaryDataTableColumnV2[] = [];
    for (let carriageIndex = 0; carriageIndex < carriagesPerRail; carriageIndex++) {
      const values: SummaryValueV2[] = source.map((item) => {
        const { value, extra, isStandard } = item.rail[railIndex].carriage[carriageIndex];
        return {
          name: item.name,
          gaName: item.name,
          value,
          extra,
          isStandard,
        };
      });

      const column: SummaryDataTableColumnV2 = {
        name: instance.t("linearConfigSummary.step7.carriage", { count: railIndex + 1 + (carriageIndex + 1) / 10 }),
        gaName: `carriage_set_${carriageIndex + 1}`,
        values,
      };
      columns.push(column);
    }
    const table: SummaryDataTableV2 = {
      name: instance.t("linearConfigSummary.step.headerGuidewaySet", { count: railIndex + 1 }),
      gaName: `guideway_set_${railIndex + 1}`,
      entries: columns,
    };
    tables.push(table);
  }

  const details: CarriageDetailsSummaryData = {
    name: "",
    gaName: "configured_carriage_data",
    tables: tables,
  };

  return details;
}

function transformSimpleTableToSelectionData(table: SimpleSummaryDataTableV2) {
  return {
    gaName: table.gaName,
    selectionData: table.entries.reduce((acc, entry) => {
      acc[prepareSummaryDataName(entry)] = prepareSummaryDataValue(entry);
      return acc;
    }, {} as GaSelectionData),
  };
}

function transformMultiColumnTableToSelectionData(table: MultiColumnSummaryDataTableV2) {
  return {
    gaName: table.gaName,
    selectionData: table.entries.reduce((acc, entry) => {
      acc[entry.gaName] = prepareSummaryDataValues(entry.values);
      return acc;
    }, {} as GaSelectionData),
  };
}

export function isSimpleTable(table: SummaryDataTableV2): table is SimpleSummaryDataTableV2 {
  return table.entries.length > 0 && "value" in table.entries[0];
}

function transformTableToSelectionData(table: SummaryDataTableV2) {
  if (isSimpleTable(table)) {
    return transformSimpleTableToSelectionData(table);
  }

  return transformMultiColumnTableToSelectionData(table);
}

export function transformToSelectionData(tables: (SummaryDataTableV2 | CarriageDetailsSummaryData)[]): GaSelectionData {
  const mappedTables = tables.map((table) => {
    if ("tables" in table) {
      return {
        gaName: table.gaName,
        selectionData: transformToSelectionData(table.tables),
      };
    } else {
      return transformTableToSelectionData(table);
    }
  });

  const selectionData: GaSelectionData = {};

  for (const table of mappedTables) {
    if (!table.gaName) {
      Object.assign(selectionData, table.selectionData);
    } else {
      selectionData[table.gaName] = table.selectionData;
    }
  }

  return selectionData;
}

export class SummaryDataProvider {
  public kmats: Dict<Kmat[]>;
  public substeps: Dict<LinearSubstep>;

  constructor(
    private state: LinearConfiguratorState,
    public t: WithTranslation["t"],
  ) {
    this.kmats = state.kmats;
    this.substeps = state.substeps;
  }

  getConfiguratorType() {
    return this.state.type;
  }

  getStepName(index: number) {
    return this.t(this.state.steps[index - 1].name);
  }

  getGaStepName(index: number): string {
    return `${index} - ${this.getStepName(index)}`;
  }

  prepareTileSubstepSummary(
    stepType: SubstepType,
    postProcessor?: TileSubstepSummaryPostProcessor,
  ): SummaryData | null {
    const step = this.state.substeps[stepType];

    if (!step || step.selectedTile === null) {
      return null;
    }
    const allTiles = [...step.tiles];
    step.groupedTiles?.forEach((group) => allTiles.push(...group.tiles));
    const selectedTile = allTiles[step.selectedTile];
    const summary: SummaryData = {
      name: this.t(step.name),
      value: this.t(selectedTile.name),
      isStandard: isTileStandard(selectedTile, this.kmats),
    };
    postProcessor?.(summary, step, this.kmats);
    return summary;
  }
}

type TileSubstepSummaryPostProcessor = (data: SummaryData, step: LinearSubstep, kmats: Dict<Kmat[]>) => void;

export function prepareSubStepsInfo(summaryProvider: SummaryDataProvider, substeps: SubstepType[]) {
  const substepData = substeps
    .map((substepType) => [substepType, summaryProvider.prepareTileSubstepSummary(substepType)] as const)
    .filter(([, summary]) => isNonNull(summary)) as [SubstepType, SummaryData][];

  if (substepData.length === 0) {
    return null;
  }

  substepData.forEach((data) => {
    data[1].value = removeMultipleTags(data[1].value, "p", "span")!.trim();
    data[1].name = removeMultipleTags(data[1].name, "p", "span")!.trim();
  });

  const data: Record<string, SummaryData> = toMapFn(
    substepData,
    ([type]) => type,
    ([, summary]) => summary,
  );

  return substeps.map((stepName) => data[stepName]).filter(Boolean) as SummaryData[];
}
export function formatNumberValue(value: string | undefined, unit?: string) {
  let val = formatCsticValueAsNumber(value, 2);
  if (unit) {
    val += unit;
  }
  return val;
}

type CsticValueGetter = (cstic: LinearCstic) => string;

export const makeNumberGetter =
  (unit?: string): CsticValueGetter =>
  (cstic) =>
    formatNumberValue(cstic.value, unit);
export const makeLabelGetter =
  (t: WithTranslation["t"]): CsticValueGetter =>
  (cstic) =>
    t(cstic.values.find((v) => v.value === cstic.value)?.label ?? "");
